// ============================================================================
// MarketPhoneStatus — состояние телефонного гейта заявки (SSoT — сервер:
// markettake/phone.go). Показывает, ЧТО именно недоступно, и понятный путь
// подтверждения: привязанный Телеграм/MAX либо оператор. СМС-путь здесь НЕ
// изобретается: подтверждение номера для заявок выполняет оператор/канал,
// а не клиентская «отправка кода».
//
// Компонент не ходит в API и не решает статус сам — получает состояние из
// серверных меток строки заявки (phone_verified/phone_unverified).
// ============================================================================

import { PhoneCall, PhoneOff, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { MarketPhoneState } from '@/lib/market';

interface MarketPhoneStatusProps {
  state: MarketPhoneState;
  audience: 'client' | 'master';
  /** Подсказка сервера (phone_verification_hint) — только у неподтверждённых. */
  hint?: string;
  /** Короткий вариант для карточки ленты. */
  compact?: boolean;
  /** Переход в профиль (контакты и подключения) — если экран его умеет. */
  onOpenProfile?: () => void;
}

const TONE: Record<MarketPhoneState, string> = {
  verified: 'border-emerald-500/30 bg-emerald-500/5',
  unverified: 'border-amber-500/40 bg-amber-500/10',
  unknown: 'border-border bg-muted/40',
};

const TITLE_TONE: Record<MarketPhoneState, string> = {
  verified: 'text-emerald-700',
  unverified: 'text-amber-800',
  unknown: 'text-foreground',
};

function title(state: MarketPhoneState): string {
  if (state === 'verified') return 'Телефон подтверждён';
  if (state === 'unverified') return 'Телефон не подтверждён';
  return 'Как подтверждается телефон';
}

function blockedItems(audience: 'client' | 'master'): string[] {
  if (audience === 'client') {
    return ['Мастера не смогут взять заявку', 'Контакты останутся скрыты'];
  }
  return ['Взятие заявки недоступно', 'Контакты заказчика не раскрываются', 'Оплата не списана'];
}

function pathSteps(audience: 'client' | 'master'): string[] {
  if (audience === 'client') {
    return [
      'Привяжите Телеграм или MAX в профиле → «Подключения».',
      'Напишите оператору в чате поддержки или в чате заявки — он подтвердит номер.',
    ];
  }
  return [
    'Номер подтверждает заказчик: Телеграм или MAX в его профиле либо обращение к оператору.',
    'После подтверждения заявка станет доступна для взятия.',
  ];
}

export function MarketPhoneStatus({
  state,
  audience,
  hint,
  compact = false,
  onOpenProfile,
}: MarketPhoneStatusProps) {
  const Icon = state === 'verified' ? ShieldCheck : state === 'unverified' ? PhoneOff : PhoneCall;
  const isMaster = audience === 'master';

  return (
    <section className={`rounded-2xl border px-3.5 py-3 ${TONE[state]}`}>
      <div className="flex items-start gap-2">
        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${TITLE_TONE[state]}`} />
        <div className="min-w-0 flex-1">
          <div className={`text-xs font-semibold ${TITLE_TONE[state]}`}>{title(state)}</div>

          {state === 'verified' && (
            <p className="mt-1 text-[11px] leading-5 text-muted-foreground">
              {isMaster
                ? 'Можно взять заявку — контакты откроются после оплаты.'
                : 'Мастера могут взять заявку; контакты откроются после взятия.'}
            </p>
          )}

          {state === 'unverified' && (
            <>
              <p className="mt-1 text-[11px] leading-5 text-muted-foreground">
                {hint || (isMaster
                  ? 'Заказчик не подтвердил номер — взятие и контакты заблокированы.'
                  : 'Пока номер не подтверждён, заявка остаётся недоступной мастерам.')}
              </p>
              {!compact && (
                <ul className="mt-1.5 list-disc space-y-0.5 pl-4 text-[11px] leading-5">
                  {blockedItems(audience).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
              {compact && (
                <div className="mt-1 text-[11px] font-medium">
                  {isMaster
                    ? 'Взятие заявки и контакты заказчика недоступны.'
                    : 'Взятие заявки мастерами и раскрытие контактов недоступны.'}
                </div>
              )}
              <div className="mt-2 text-[11px] leading-5">
                <span className="font-medium">Как подтвердить:</span>
                <ol className="mt-1 list-decimal space-y-0.5 pl-4">
                  {pathSteps(audience).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ol>
              </div>
              {onOpenProfile && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2 h-8 rounded-lg text-[11px]"
                  onClick={onOpenProfile}
                >
                  Открыть профиль
                </Button>
              )}
            </>
          )}

          {state === 'unknown' && (
            <>
              <p className="mt-1 text-[11px] leading-5 text-muted-foreground">
                {isMaster
                  ? 'Статус номера проверяется сервером при взятии заявки.'
                  : 'Статус номера проверяется сервером при взятии заявки. Если номер не подтверждён, мастера не смогут взять заявку, а контакты останутся скрыты.'}
              </p>
              <div className="mt-2 text-[11px] leading-5">
                <span className="font-medium">Как подтвердить:</span>
                <ol className="mt-1 list-decimal space-y-0.5 pl-4">
                  {pathSteps(audience).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ol>
              </div>
              {onOpenProfile && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2 h-8 rounded-lg text-[11px]"
                  onClick={onOpenProfile}
                >
                  Открыть профиль
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
